import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import CanUseFeature, { useCanUseFeature } from '@dashboard/components/CanUseFeature';
import { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { Checkbox, TextInput } from '@dashboard/components/Formik';
import FormError from '@dashboard/components/Formik/components/FormError';
import TabButton from '@dashboard/components/TabButton';
import { usePreview } from '@dashboard/context/PreviewContext';
import Features from '@dashboard/enums/features';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import ConfirmationCheckoutMessageEditor from './ConfirmationCheckoutMessageEditor';
import SendTestEmailButton from './SendTestEmailButton';
import usePage from '@dashboard/hooks/usePage';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import getBillingModel, { BILLING_MODELS } from '@dashboard/services/getBillingModel';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

// The buyer's options and bumps are unknown here, so the default price alone decides.
function isFreeCheckout(page) {
  const prices = (page.product?.prices ?? []).filter(price => price?.enabled !== false);
  const price =
    prices.find(candidate => String(candidate._id) === String(page.product?.defaultPriceId)) ??
    prices[0];

  return (
    Boolean(price) && !price.amount && !price.setupFee && !(page.fees ?? []).some(fee => fee.amount)
  );
}

export function getDefaultConfirmationEmailSubject(page) {
  if (page.type === PAGE_TYPES.FORM) {
    return page.title ? `Submission confirmation for ${page.title}` : 'Submission confirmed';
  } else if (page.type === PAGE_TYPES.CHECKOUT) {
    const { product } = page;
    const billingModel = getBillingModel(product);

    if (billingModel === BILLING_MODELS.SUBSCRIPTION) {
      return `Subscription confirmation for ${product.title}`;
    }
    if (billingModel === BILLING_MODELS.CHARGE && isFreeCheckout(page)) {
      return `Submission confirmation for ${product.title}`;
    }
    return `Order confirmation for ${product.title}`;
  } else if (page.type === PAGE_TYPES.EVENT) {
    return `Booking confirmation for ${page.title || page.name}`;
  } else {
    return '';
  }
}

function getTabIndex({ sendEmailConfirmation, customizeEmailConfirmation }) {
  if (!sendEmailConfirmation) {
    return 2;
  }

  if (customizeEmailConfirmation) return 1;

  return 0;
}

const EmailConfirmationForm = () => {
  const { page, updatePage } = usePage();

  const canCustomizeEmailConfirmation = useCanUseFeature(Features.CUSTOMIZE_EMAIL_CONFIRMATION);
  const canDisableEmailConfirmation = useCanUseFeature(Features.OPTIONAL_EMAIL_CONFIRMATION);
  const { sendTestEmail } = usePageQueries();

  const [{ previewPriceId }, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const defaultConfirmationEmailSubject = getDefaultConfirmationEmailSubject(page);

  const sendConfirmationTestEmail = () => sendTestEmail(page._id, previewPriceId || undefined);

  useEffect(() => {
    if (page.sendEmailConfirmation) {
      setPreview({
        type: 'confirmationEmailMessage',
      });
    }

    return () =>
      setPreview({
        type: 'checkout',
      });
  }, []);

  const pageValues = {
    sendEmailConfirmation: page.sendEmailConfirmation,
    customizeEmailConfirmation: page.customizeEmailConfirmation,
    confirmationEmailSubject: page.confirmationEmailSubject || defaultConfirmationEmailSubject,
    confirmationEmailMessage: page.confirmationEmailMessage,
    confirmationEmailShowLogo: page.confirmationEmailShowLogo,
    confirmationEmailShowStoreName: page.confirmationEmailShowStoreName,
  };

  return (
    <Formik
      initialValues={pageValues}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const { data, error } = await updatePage.mutateAsync(values);

        if (data) {
          if (data.sendEmailConfirmation) {
            setPreview({
              type: 'confirmationEmailMessage',
            });
          } else {
            setPreview({
              type: 'checkout',
            });
          }

          actions.setStatus('');
          actions.resetForm({
            values: {
              sendEmailConfirmation: data.sendEmailConfirmation,
              confirmationEmailSubject: data.confirmationEmailSubject,
              confirmationEmailMessage: data.confirmationEmailMessage,
              customizeEmailConfirmation: data.customizeEmailConfirmation,
              confirmationEmailShowLogo: data.confirmationEmailShowLogo,
              confirmationEmailShowStoreName: data.confirmationEmailShowStoreName,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, setFieldValue, handleSubmit, setValues, isSubmitting }) => {
        let tabIndex = getTabIndex(values);

        // The "Custom email" and "No email" tabs are removed when the seller can't use them,
        // so fall back to the first tab rather than selecting a tab that no longer exists.
        if (!canCustomizeEmailConfirmation && tabIndex === 1) tabIndex = 0;
        if (!canDisableEmailConfirmation && tabIndex === 2) tabIndex = 0;

        return (
          <Form onSubmit={handleSubmit}>
            <UnsavedChangesReporter />
            <TabGroup
              defaultIndex={tabIndex}
              onChange={index => {
                switch (index) {
                  case 0:
                    setValues({
                      ...pageValues,
                      sendEmailConfirmation: true,
                      customizeEmailConfirmation: false,
                    });
                    break;
                  case 1:
                    setValues({
                      ...pageValues,
                      sendEmailConfirmation: true,
                      customizeEmailConfirmation: true,
                    });
                    break;
                  case 2:
                    setValues({
                      ...pageValues,
                      sendEmailConfirmation: false,
                      customizeEmailConfirmation: false,
                    });
                    break;
                }
              }}
            >
              <TabList className="flex mt-1 gap-x-2">
                <Tab className="w-1/3 outline-none">
                  {({ selected }) => <TabButton selected={selected}>Email confirmation</TabButton>}
                </Tab>
                {canCustomizeEmailConfirmation ? (
                  <Tab className="w-1/3 outline-none">
                    {({ selected }) => <TabButton selected={selected}>Custom email</TabButton>}
                  </Tab>
                ) : (
                  <div className="w-1/3">
                    <CanUseFeature feature={Features.CUSTOMIZE_EMAIL_CONFIRMATION} inset fill>
                      <TabButton>Custom email</TabButton>
                    </CanUseFeature>
                  </div>
                )}
                {canDisableEmailConfirmation ? (
                  <Tab className="w-1/3 outline-none">
                    {({ selected }) => <TabButton selected={selected}>No email</TabButton>}
                  </Tab>
                ) : (
                  <div className="w-1/3">
                    <CanUseFeature
                      feature={Features.OPTIONAL_EMAIL_CONFIRMATION}
                      inset
                      fill
                      description="Confirmation emails are required on the free plan. Upgrade to make them optional."
                    >
                      <TabButton>No email</TabButton>
                    </CanUseFeature>
                  </div>
                )}
              </TabList>

              <TabPanels>
                <TabPanel className="mt-4">
                  <FormElement>
                    <Label>Show on email</Label>
                    <Checkbox.Group>
                      <Checkbox.Wrapper>
                        <Checkbox name="confirmationEmailShowLogo" id="confirmationEmailShowLogo" />
                        <Checkbox.Label htmlFor="confirmationEmailShowLogo">Logo</Checkbox.Label>
                      </Checkbox.Wrapper>

                      <Checkbox.Wrapper>
                        <Checkbox
                          name="confirmationEmailShowStoreName"
                          id="confirmationEmailShowStoreName"
                        />
                        <Checkbox.Label htmlFor="confirmationEmailShowStoreName">
                          Store name
                        </Checkbox.Label>
                      </Checkbox.Wrapper>
                    </Checkbox.Group>
                  </FormElement>

                  <FormElement>{status && status}</FormElement>

                  <FormSubmit>
                    <div className="flex flex-wrap gap-y-4 justify-between w-full">
                      <div className="flex items-baseline whitespace-nowrap gap-x-2">
                        <FormSubmit.Button>Save</FormSubmit.Button>
                        <FormSubmit.State visible={!!dirty && !isSubmitting} />
                      </div>

                      <div className="ml-auto flex gap-x-2 items-start">
                        <SendTestEmailButton onSend={sendConfirmationTestEmail} />
                      </div>
                    </div>
                  </FormSubmit>
                </TabPanel>

                {canCustomizeEmailConfirmation && (
                  <TabPanel className="mt-4">
                    <FormElement>
                      <Label htmlFor="confirmationEmailSubject">Subject</Label>
                      <TextInput
                        required
                        id="confirmationEmailSubject"
                        name="confirmationEmailSubject"
                        type="text"
                        value={values.confirmationEmailSubject}
                      />
                      <FormError name="confirmationEmailSubject" />
                    </FormElement>

                    <FormElement>
                      <div className="flex justify-between items-baseline">
                        <Label htmlFor="confirmationEmailMessage" tags={['supports HTML']}>
                          Email body
                        </Label>
                        <span className="text-xs text-muted-foreground font-normal text-right ml-auto">
                          Insert variables by typing{' '}
                          <code className="font-mono bg-muted px-1 py-0.5 text-primary">{'{'}</code>
                        </span>
                      </div>

                      <ConfirmationCheckoutMessageEditor
                        required
                        id="confirmationEmailMessage"
                        name="confirmationEmailMessage"
                        initialValue="Thank you for your payment"
                        page={page}
                        values={values}
                        setFieldValue={setFieldValue}
                        confirmationEmail
                      />
                      <FormError name="confirmationEmailMessage" />
                    </FormElement>

                    <FormElement>
                      <Label>Show on email</Label>
                      <Checkbox.Group>
                        <Checkbox.Wrapper>
                          <Checkbox
                            name="confirmationEmailShowLogo"
                            id="confirmationEmailShowLogo"
                          />
                          <Checkbox.Label htmlFor="confirmationEmailShowLogo">Logo</Checkbox.Label>
                        </Checkbox.Wrapper>

                        <Checkbox.Wrapper>
                          <Checkbox
                            name="confirmationEmailShowStoreName"
                            id="confirmationEmailShowStoreName"
                          />
                          <Checkbox.Label htmlFor="confirmationEmailShowStoreName">
                            Store name
                          </Checkbox.Label>
                        </Checkbox.Wrapper>
                      </Checkbox.Group>
                    </FormElement>

                    <FormElement>{status && status}</FormElement>

                    <FormSubmit>
                      <div className="flex flex-wrap gap-y-4 justify-between w-full">
                        <div className="flex items-baseline whitespace-nowrap gap-x-2">
                          <FormSubmit.Button>Save</FormSubmit.Button>
                          <FormSubmit.State visible={!!dirty && !isSubmitting} />
                        </div>

                        <div className="ml-auto flex gap-x-2 items-start">
                          <SendTestEmailButton onSend={sendConfirmationTestEmail} />
                        </div>
                      </div>
                    </FormSubmit>
                  </TabPanel>
                )}

                {canDisableEmailConfirmation && (
                  <TabPanel>
                    <FormSubmit>
                      <FormSubmit.Button>Save</FormSubmit.Button>
                      <FormSubmit.State visible={!!dirty && !isSubmitting} />
                    </FormSubmit>
                  </TabPanel>
                )}
              </TabPanels>
            </TabGroup>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EmailConfirmationForm;
